import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Briefcase, Trash2, Save, ChevronRight, ExternalLink, X } from 'lucide-react';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';

interface BrokerOption {
  id: string;
  brokerage_name: string;
  fsp_number?: string;
  active?: boolean;
}

interface Client {
  id: string;
  name: string;
  trading_name?: string;
  primary_contact_name?: string;
  primary_contact_email?: string;
  primary_contact_phone?: string;
  industry?: string;
  broker_id?: string;
  broker_name?: string | null;
  insurer_id?: string;
  physical_address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  latitude?: number | null;
  longitude?: number | null;
  place_id?: string | null;
  active?: boolean;
  site_count?: number;
  created_at?: string;
}

const emptyForm = {
  name: '', trading_name: '', primary_contact_name: '', primary_contact_email: '',
  primary_contact_phone: '', industry: '', physical_address: '', broker_id: '',
  // Location — auto-filled by Google Places when a suggestion is
  // picked; visible so operators can eyeball what got captured and
  // later filter clients by province / town.
  city: '',
  province: '',
  postal_code: '',
  place_id: '' as string,
  latitude: null as number | null,
  longitude: null as number | null,
};

export const ClientsPage: React.FC = () => {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState<Client[]>([]);
  const [brokers, setBrokers] = useState<BrokerOption[]>([]);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await api.get<Client[]>('/clients', { params: q ? { q } : {} });
      setClients(r.data);
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to load clients');
    }
  };

  const loadBrokers = async (): Promise<void> => {
    try {
      const r = await api.get<BrokerOption[]>('/brokers');
      // Only show active brokers in the picker — inactive ones shouldn't
      // be assignable to new clients. But if we're editing a client that
      // was linked to a now-inactive broker, we still want to show it as
      // "selected" so the operator can see the current link.
      setBrokers(r.data);
    } catch {
      /* silent — the field is optional */
    }
  };

  useEffect(() => { void load(); void loadBrokers(); /* eslint-disable-line */ }, []);
  useEffect(() => { const t = setTimeout(() => void load(), 250); return () => clearTimeout(t); /* eslint-disable-line */ }, [q]);

  // Deep-link: BrokerDetail's "Add / manage clients" button passes
  // ?broker_id=BRK-xxx. When we land here with that param, auto-open
  // the create form with that broker preselected.
  useEffect(() => {
    const preselectBroker = searchParams.get('broker_id');
    if (preselectBroker && !creating && !editingId) {
      setForm((f) => ({ ...f, broker_id: preselectBroker }));
      setCreating(true);
      // Clean the URL so a refresh doesn't re-trigger.
      searchParams.delete('broker_id');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/clients/${editingId}`, form);
        toast.success('Client updated');
      } else {
        await api.post('/clients', form);
        toast.success('Client created');
      }
      setForm(emptyForm); setCreating(false); setEditingId(null);
      void load();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (c: Client) => {
    setEditingId(c.id);
    setForm({
      name: c.name || '', trading_name: c.trading_name || '',
      primary_contact_name: c.primary_contact_name || '',
      primary_contact_email: c.primary_contact_email || '',
      primary_contact_phone: c.primary_contact_phone || '',
      industry: c.industry || '', physical_address: c.physical_address || '',
      broker_id: c.broker_id || '',
      city: c.city || '',
      province: c.province || '',
      postal_code: c.postal_code || '',
      place_id: c.place_id || '',
      latitude: c.latitude ?? null,
      longitude: c.longitude ?? null,
    });
    setCreating(true);
  };

  const remove = async (id: string) => {
    if (!confirm(`Delete this client and all of its sites? This cannot be undone.`)) return;
    try {
      const r = await api.delete<{ sites_deleted: number }>(`/clients/${id}`);
      toast.success(`Client deleted (${r.data.sites_deleted} sites removed)`);
      void load();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Delete failed');
    }
  };

  const displayedClients = useMemo(() => clients, [clients]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Clients</h2>
          <p className="text-sm text-raptor-navy/60 font-body">
            First-class business entities that own sites &amp; policies. Every survey instruction, import row and analytics record links back to a client.
          </p>
        </div>
        <Button variant="gradient" onClick={() => { setEditingId(null); setForm(emptyForm); setCreating(!creating); }} data-testid="new-client-btn">
          <Plus size={16} className="mr-1" /> {creating ? 'Cancel' : 'New client'}
        </Button>
      </div>

      {creating && (
        <Card>
          <CardHeader>
            <CardTitle className="font-headline text-xl text-raptor-navy">{editingId ? 'Edit client' : 'New client'}</CardTitle>
            <CardDescription>Fill the required name; every other field can be added later.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {/* ─── 1. Identity ────────────────────────────────── */}
              <div><Label>Client name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" /></div>
              <div><Label>Trading name</Label><Input value={form.trading_name} onChange={(e) => setForm({ ...form, trading_name: e.target.value })} /></div>
              <div><Label>Industry</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="Retail, Hospitality, Cash-in-Transit…" /></div>
              <div>
                <Label>Broker <span className="text-raptor-navy/50 text-xs font-normal">(optional)</span></Label>
                <Select
                  data-testid="client-broker-select"
                  value={form.broker_id}
                  onChange={(e) => setForm({ ...form, broker_id: e.target.value })}
                >
                  <option value="">— No broker assigned —</option>
                  {brokers.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.brokerage_name}{b.fsp_number ? ` (FSP ${b.fsp_number})` : ''}{b.active === false ? ' · inactive' : ''}
                    </option>
                  ))}
                  {/* If editing an existing client whose broker is no
                      longer in the list (deleted / out-of-scope), keep
                      the current id visible as a fallback option. */}
                  {form.broker_id && !brokers.some((b) => b.id === form.broker_id) && (
                    <option value={form.broker_id}>{form.broker_id} (not in your scope)</option>
                  )}
                </Select>
                {form.broker_id && (
                  <p className="text-[11px] text-raptor-navy/50 font-body pt-0.5">
                    Client will be billed &amp; surveys attributed to this broker.
                  </p>
                )}
              </div>

              {/* ─── 2. Primary contact ─────────────────────────── */}
              <div className="md:col-span-2 pt-1 border-t border-border/40 mt-2">
                <div className="text-[10px] uppercase tracking-wide text-raptor-navy/60 font-body mb-2 mt-2">Primary contact</div>
              </div>
              <div><Label>Contact name</Label><Input value={form.primary_contact_name} onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })} /></div>
              <div><Label>Contact email</Label><Input value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} type="email" /></div>
              <div className="md:col-span-2"><Label>Contact phone</Label><Input value={form.primary_contact_phone} onChange={(e) => setForm({ ...form, primary_contact_phone: e.target.value })} /></div>

              {/* ─── 3. Location (address first, drives autofill) ── */}
              <div className="md:col-span-2 pt-1 border-t border-border/40 mt-2">
                <div className="text-[10px] uppercase tracking-wide text-raptor-navy/60 font-body mb-2 mt-2">Location</div>
                <Label>Physical address</Label>
                <AddressAutocomplete
                  value={form.physical_address}
                  onChange={(v: string) => setForm({ ...form, physical_address: v })}
                  onResolve={(p) => {
                    // Autofill city/province/postal + coords + place_id
                    // from the picked suggestion so operators can filter
                    // clients by territory later without extra typing.
                    setForm((f) => ({
                      ...f,
                      physical_address: p.formatted_address,
                      city: p.city || f.city,
                      province: p.province || f.province,
                      postal_code: p.postal_code || f.postal_code,
                      latitude: p.lat,
                      longitude: p.lng,
                      place_id: p.place_id || '',
                    }));
                  }}
                  testid="client-address"
                  placeholder="Start typing to auto-fill city, province, postal code and GPS…"
                />
                {form.latitude != null && form.longitude != null && (
                  <div className="mt-1 text-[11px] text-emerald-700 font-tabular">
                    ✓ Coords captured: {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
                  </div>
                )}
              </div>
              <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="client-city" /></div>
              <div><Label>Province</Label><Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} data-testid="client-province" /></div>
              <div><Label>Postal code</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="client-postal" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => { setCreating(false); setEditingId(null); setForm(emptyForm); }}>Cancel</Button>
              <Button variant="gradient" onClick={() => void submit()} disabled={saving} data-testid="client-save-btn"><Save size={14} className="mr-1" /> {saving ? 'Saving…' : editingId ? 'Update client' : 'Create client'}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-2">
          <CardTitle className="font-headline">All clients ({clients.length})</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-raptor-navy/40" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-7 h-8 w-56" data-testid="client-search" />
            </div>
            {q && (
              <Button variant="ghost" size="sm" onClick={() => setQ('')} data-testid="client-reset-filters">
                <X size={14} /> Reset filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {displayedClients.length === 0 ? (
            <div className="p-10 text-center text-raptor-navy/50 font-body">
              <Briefcase size={40} className="mx-auto mb-2 opacity-40" />
              No clients yet. Click <b>New client</b> to create the first one.
            </div>
          ) : (
            <table className="w-full text-sm font-body">
              <thead className="bg-raptor-neutral/40 text-left text-raptor-navy/70">
                <tr>
                  <th className="py-2 px-3">Name</th>
                  <th className="py-2 px-3">Industry</th>
                  <th className="py-2 px-3">Contact</th>
                  <th className="py-2 px-3">Sites</th>
                  <th className="py-2 px-3">Broker</th>
                </tr>
              </thead>
              <tbody>
                {displayedClients.map((c) => (
                  <tr
                    key={c.id}
                    className="border-t border-border/60 hover:bg-raptor-neutral/30 cursor-pointer"
                    data-testid={`client-row-${c.id}`}
                    onClick={() => nav(`/insurer/clients/${c.id}`)}
                  >
                    <td className="py-2 px-3">
                      <div className="font-medium text-raptor-navy inline-flex items-center gap-1">
                        {c.name}
                        <ChevronRight size={12} className="text-raptor-navy/30" />
                      </div>
                      {c.trading_name && <div className="text-xs text-raptor-navy/50">t/a {c.trading_name}</div>}
                    </td>
                    <td className="py-2 px-3 text-raptor-navy/70">{c.industry ?? '—'}</td>
                    <td className="py-2 px-3 text-raptor-navy/70">
                      {c.primary_contact_name || '—'}
                      {c.primary_contact_email && <div className="text-xs text-raptor-navy/50">{c.primary_contact_email}</div>}
                    </td>
                    <td className="py-2 px-3"><Badge variant="muted">{c.site_count ?? 0}</Badge></td>
                    <td className="py-2 px-3 text-raptor-navy/70 text-xs">
                      {c.broker_id ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-0.5 hover:text-raptor-blue hover:underline"
                          onClick={(e) => { e.stopPropagation(); nav(`/insurer/brokers/${c.broker_id}`); }}
                        >
                          {c.broker_name || c.broker_id}
                          <ExternalLink size={10} />
                        </button>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientsPage;
